import { unsafeCSS, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import ResizeObserver from 'resize-observer-polyfill';

import { PopBaseComponent } from '../../base/pop-base-component';
import type { TeamMember } from './team-member.model';

import style from './team-member.scss?inline';
import { Constants } from './team-member.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import { IconSVG } from '../../common/utils/icon';

/**
 * @tag pop-team-member-component
 * @attribute {TeamMember} [data-component-object] Team Member content
 */
@customElement(Constants.TAG_NAME)
export class PopTeamMemberComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * Product country
   */
  @property({
    attribute: BaseConstants.DATA_COUNTRY,
    type: String,
  })
  protected country = 'us';

  /**
   * Product language
   */
  @property({
    attribute: BaseConstants.DATA_LANGUAGE,
    type: String,
  })
  protected language = 'en';

  /**
   * @internal
   */
  @state()
  private breakpointIsXLUp = false;

  /**
   * @internal
   */
  private resizeObserver!: ResizeObserver;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  componentObject?: TeamMember;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(
      this.updateBreakpointStatus.bind(this),
    );
    this.resizeObserver?.observe(document.body);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
  }

  updateBreakpointStatus() {
    const computedStyle = getComputedStyle(this);
    const xLargeBreakpoint = computedStyle.getPropertyValue(
      Constants.BEACON_SYSTEM_BREAKPOINT_X_LARGE,
    );

    this.breakpointIsXLUp = window.matchMedia(
      `(min-width: ${xLargeBreakpoint})`,
    ).matches;
  }

  renderTeamMemberImage() {
    const stockAvatarDataURL = IconSVG.stockAvatarDataURL();
    const quotedStockAvatarDataURL = `"${stockAvatarDataURL}"`;

    return html`
      <div class="beacon-contact-tile__icon">
        <img
          src="${this.componentObject?.imageSource || stockAvatarDataURL}"
          alt=""
          height="128"
          width="128"
          loading="lazy"
          onerror="
          this.onerror = null;
          this.src = ${quotedStockAvatarDataURL};
        "
        />
      </div>
    `;
  }

  renderTeamMemberName() {
    if (this.componentObject?.name) {
      return html`
        <h3 class="beacon-contact-tile__title">
          <a href="${this.getBioLink()}" target="_blank"
            >${this.componentObject.name}</a
          >
        </h3>
      `;
    }

    return html``;
  }

  renderTeamMemberJobTitle() {
    if (this.componentObject?.jobTitle) {
      return html`
        <div class="beacon-contact-tile__job-title b-flow-space">
          <p>
            ${this.componentObject.jobTitle} -
            ${this.componentObject.yearsOfExperience} years
          </p>
        </div>
      `;
    }

    return html``;
  }

  // Using 16-14-14-14 for buttons font size for now
  getLinkScale() {
    return this.breakpointIsXLUp ? Constants.NORMAL : Constants.MINI;
  }

  getBioLink() {
    const bioKey = this.componentObject?.bioKey;

    return `/financial-intermediary/${this.country}/${this.language}/search.html?q=${bioKey}`;
  }

  renderBioLink() {
    if (this.componentObject?.bioKey) {
      return html`
        <beacon-button
          variant="tertiary"
          scale="${this.getLinkScale()}"
          link-href="${this.getBioLink()}"
          link-target="_blank"
          data-test-id="bio-link"
        >
          ${Constants.VIEW_BIO}
        </beacon-button>
      `;
    }

    return html``;
  }

  renderInsightsLink() {
    return html`
      <beacon-button
        variant="tertiary"
        scale="${this.getLinkScale()}"
        link-href="#insights"
        link-target="_self"
        data-test-id="insights-link"
      >
        ${Constants.DISCOVER_INSIGHTS}
      </beacon-button>
    `;
  }

  render() {
    if (!this.componentObject) {
      return html``;
    }
    return this.renderComponent(html`
      <div>
        <beacon-contact-tile class="beacon-contact-tile__vertical-fill">
          <div slot="body">
            ${this.renderTeamMemberImage()} ${this.renderTeamMemberName()}
            ${this.renderTeamMemberJobTitle()}
          </div>
          <div slot="footer">
            <div class="links-container b-margin-block-start--micro--normal">
              ${this.renderBioLink()} ${this.renderInsightsLink()}
            </div>
          </div>
        </beacon-contact-tile>
      </div>
    `);
  }
}
